import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { X, Plus, Trash2, Layers, Save } from "lucide-react";
import { useSelector } from "react-redux";
import { selectUserID } from "../../../../redux/IchthusSlice";
import { domain } from "../../../../security";
import Loader from "../../../loader/Loader";

const ManageBomModal = ({ onClose, refreshData }) => {
  const currentUserID = useSelector(selectUserID);
  const [isLoading, setIsLoading] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [regularProducts, setRegularProducts] = useState([]); // For Ingredients (Regular only)
  const [bomProducts, setBomProducts] = useState([]); // For Output Product (BOM only)

  // ── BOM Header State ──
  const [bomName, setBomName] = useState("");
  const [bomProductId, setBomProductId] = useState("");
  const [description, setDescription] = useState("");

  // ── Ingredient Form State ──
  const [selectedIngredientId, setSelectedIngredientId] = useState("");
  const [selectedUomId, setSelectedUomId] = useState("");
  const [quantity, setQuantity] = useState("");
  const [ingredientUoms, setIngredientUoms] = useState([]);

  // ── Added Ingredients List ──
  const [components, setComponents] = useState([]);

  useEffect(() => {
    fetchProductsByType();
  }, []);

  const fetchProductsByType = async () => {
    setIsLoading(true);
    try {
      // Fetch Regular products for Ingredients
      const regularRes = await axios.get(
        `${domain}/api/Products/by-item-type-category?category=Regular`,
      );
      setRegularProducts(regularRes.data);

      // Fetch BOM products for Output Product
      const bomRes = await axios.get(
        `${domain}/api/Products/by-item-type-category?category=BOM`,
      );
      setBomProducts(bomRes.data);

      // Store all products for reference
      setAllProducts(bomRes.data);
    } catch (err) {
      toast.error("Failed to load products.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch valid UOMs when an ingredient is selected
  const handleIngredientChange = async (e) => {
    const ingredientId = e.target.value;
    setSelectedIngredientId(ingredientId);
    setSelectedUomId("");
    setIngredientUoms([]);

    if (ingredientId) {
      try {
        const res = await axios.get(
          `${domain}/api/Products/${ingredientId}/pricing-uoms`,
        );
        setIngredientUoms(res.data);
      } catch (err) {
        toast.error("Failed to load UOMs for this ingredient.");
      }
    }
  };

  // Add the ingredient to our local React list before saving
  const handleAddIngredientToList = () => {
    if (!selectedIngredientId || !selectedUomId || !quantity || quantity <= 0) {
      toast.warning("Please fill all ingredient fields.");
      return;
    }

    const product = regularProducts.find(
      (p) => p.id === Number(selectedIngredientId),
    );
    const uom = ingredientUoms.find((u) => u.id === Number(selectedUomId));

    if (components.some((c) => c.productId === product.id)) {
      toast.warning("This ingredient is already in the list.");
      return;
    }

    setComponents([
      ...components,
      {
        productId: product.id,
        productName: product.productName,
        itemCode: product.itemCode,
        uomId: uom.id,
        uomName: uom.name,
        quantity: Number(quantity),
      },
    ]);

    // Reset ingredient fields
    setSelectedIngredientId("");
    setSelectedUomId("");
    setQuantity("");
    setIngredientUoms([]);
  };

  const handleRemoveIngredient = (productId) => {
    setComponents(components.filter((c) => c.productId !== productId));
  };

  // Submit the entire BOM (Header + Components) in one API call
  const handleSaveBom = async () => {
    if (!bomName.trim()) {
      toast.warning("BOM Name is required.");
      return;
    }
    if (!bomProductId) {
      toast.warning("Please select the Output Product.");
      return;
    }
    if (components.length === 0) {
      toast.warning("Please add at least one ingredient to the BOM.");
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        bomName: bomName.trim(),
        description: description.trim(),
        bomProductId: Number(bomProductId),
        isActive: true,
        components: components.map((c) => ({
          componentProductId: c.productId,
          quantity: c.quantity,
          uomId: c.uomId,
        })),
        userID: currentUserID,
      };

      await axios.post(`${domain}/api/ProductBom`, payload);
      toast.success("BOM created successfully!");
      if (refreshData) refreshData();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create BOM.");
      setIsLoading(false);
    }
  };

  // Filter out the selected output product so it can't be an ingredient of itself
  const availableIngredients = regularProducts.filter(
    (p) => p.id !== Number(bomProductId),
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-100 flex justify-between items-center bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-xl">
              <Layers size={20} className="text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">Add New BOM</h2>
              <p className="text-xs text-slate-500 font-medium">
                Create a bundle from your existing products
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-xl transition"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading && <Loader />}

          {/* ── STEP 1: BOM Details ── */}
          <div className="mb-6">
            <h3 className="text-sm font-bold text-slate-800 mb-3">
              1. BOM Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white border border-slate-200 p-4 rounded-2xl shadow-sm">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  BOM Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={bomName}
                  onChange={(e) => setBomName(e.target.value)}
                  className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="e.g., Concrete Mix for Pools"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  Output Product (Inventory Item){" "}
                  <span className="text-red-500">*</span>
                </label>
                <select
                  value={bomProductId}
                  onChange={(e) => setBomProductId(e.target.value)}
                  className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">-- Select BOM Product --</option>
                  {bomProducts.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.productName} ({p.itemCode}) - BOM Type
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-400 mt-1">
                  Output must be a BOM type product only
                </p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  Description (Optional)
                </label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="e.g., Special mixture with extra cement for water resistance"
                />
              </div>
            </div>
          </div>

          {/* ── STEP 2: Add Ingredients ── */}
          <div className="mb-6">
            <h3 className="text-sm font-bold text-slate-800 mb-3">
              2. Add Items (Regular Products Only)
            </h3>
            <div className="bg-purple-50 border border-purple-100 rounded-2xl p-4">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                {/* Select Product */}
                <div className="md:col-span-5">
                  <label className="block text-xs font-semibold text-slate-600 mb-1">
                    Items <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={selectedIngredientId}
                    onChange={handleIngredientChange}
                    className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Search / Select Regular Product...</option>
                    {availableIngredients.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.productName} ({p.itemCode})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Select UOM */}
                <div className="md:col-span-3">
                  <label className="block text-xs font-semibold text-slate-600 mb-1">
                    Unit (UOM) <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={selectedUomId}
                    onChange={(e) => setSelectedUomId(e.target.value)}
                    disabled={!selectedIngredientId}
                    className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm bg-white disabled:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Select UOM...</option>
                    {ingredientUoms.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name} ({u.type})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Quantity */}
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-slate-600 mb-1">
                    Quantity <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="e.g. 2"
                  />
                </div>

                {/* Add button */}
                <div className="md:col-span-2">
                  <button
                    onClick={handleAddIngredientToList}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 transition shadow-sm"
                  >
                    <Plus size={16} /> Add
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* ── STEP 3: Ingredients List ── */}
          <div>
            <h3 className="text-sm font-bold text-slate-800 mb-3">
              3. BOM Item Preview
            </h3>
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="px-5 py-3 text-xs font-semibold text-slate-400 uppercase">
                      Product
                    </th>
                    <th className="px-5 py-3 text-xs font-semibold text-slate-400 uppercase text-center">
                      Qty
                    </th>
                    <th className="px-5 py-3 text-xs font-semibold text-slate-400 uppercase text-center">
                      Unit
                    </th>
                    <th className="px-5 py-3 text-xs font-semibold text-slate-400 uppercase text-right">
                      Remove
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {components.length === 0 ? (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-5 py-8 text-center text-slate-400 italic"
                      >
                        No ingredients added yet.
                      </td>
                    </tr>
                  ) : (
                    components.map((c) => (
                      <tr
                        key={c.productId}
                        className="hover:bg-slate-50 transition"
                      >
                        <td className="px-5 py-3">
                          <div>
                            <p className="font-semibold text-slate-700">
                              {c.productName}
                            </p>
                            <p className="text-xs text-slate-400 font-mono">
                              {c.itemCode}
                            </p>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-center font-bold text-slate-800 bg-slate-50">
                          {c.quantity}
                        </td>
                        <td className="px-5 py-3 text-center text-slate-600">
                          {c.uomName}
                        </td>
                        <td className="px-5 py-3 text-right">
                          <button
                            onClick={() => handleRemoveIngredient(c.productId)}
                            className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2.5 text-sm font-semibold bg-white border border-slate-300 rounded-xl text-slate-700 hover:bg-slate-100 shadow-sm transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveBom}
            disabled={
              isLoading || components.length === 0 || !bomName || !bomProductId
            }
            className="px-6 py-2.5 text-sm font-semibold bg-purple-600 hover:bg-purple-700 text-white rounded-xl shadow-sm transition disabled:opacity-50 flex items-center gap-2"
          >
            <Save size={16} /> Save BOM
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManageBomModal;
